import LargePopupModalContainer from "../../Containers/LargePopupModalContainer";
import { BackButton } from "../../Buttons/BackButton";
import { Colors } from "../../../utils/colors";
import RectangleButton from "../../Buttons/RectangleButton";
import CustomTextField from "../../CustomTextField";
import { useCreateEventManagement } from "../../../hooks/useCreateEventManagement";
import { useCallback, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import FormBuilderWrapper from "../../Form/FormBuilderWrapper";
import {
  Event,
  EventType,
  JSONContent,
  MediaAspectRatio,
  OnboardingFlowType,
  SavedFormQuestion,
  Visibility,
} from "@markit/common.types";
import SwitchToggleItem from "../../SwitchToggleItem";
import CreateImageModal from "../../CreateEvent/CreateModals/CreateImageModal";
import { firebaseStorage, listAll, ref } from "../../../firebase";
import useAsyncOnMount from "../../../hooks/useAsyncEffectOnMount";
import { filteredSampleImages } from "../../../utils/photoUtils";
import {
  checkValidUrl,
  detectedLinkShortener,
  isSubscriptionPaymentFailed,
  getThumbnailImageUrl,
} from "@markit/common.utils";
import isEqual from "lodash.isequal";
import { omit } from "lodash";
import { useSelector, useDispatch } from "react-redux";
import {
  accountActions,
  getAccountState,
} from "../../../redux/slices/accountSlice";
import PaymentFailedButtonModal from "../../DisplayModal/UpdatePaymentInfoModal";
import ConfirmDeleteModal from "../../Containers/ConfirmPopups/ConfirmDeleteModal";
import { getEventFormQuestions } from "../../../utils/eventUtils/formUtils";
import AlertContainer from "../../Containers/AlertContainer";
import ThemeVisibilityOptions from "../Products/Components/ThemeVisibilityOptions";
import CustomRichTextEditor from "../../TextFields/CustomRichTextEditor";
import { useEditorState } from "@tiptap/react";
import { useTheme } from "../../../hooks/useTheme";
import { useRichTextEditor } from "../../../hooks/useRichTextEditor";
import { validateProductBody } from "../../../utils/productUtils";
import MediaAspectRatioDropdown from "../Products/Components/MediaAspectRatioDropdown";
import OnboardingTourStep from "../../Onboarding/OnboardingTourStep";
import { COLLECT_INFO_ONBOARDING_STEPS } from "../../../utils/onboardingFlowUtils";

type CreateFormAndTrackingLinkModalProps = {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  existingForm?: Event;
  isCloning?: boolean;
  eventType: EventType;
  onConfirmation?: (linkId: string) => void; // Used to pass back the created/updated form/link to the parent component to update local state
  defaultPublicLink?: boolean; // if we want to default the link to public
};

const CreateFormAndTrackingLinkModal = (
  props: CreateFormAndTrackingLinkModalProps
) => {
  const {
    isVisible,
    setIsVisible,
    existingForm,
    isCloning,
    eventType,
    onConfirmation,
    defaultPublicLink,
  } = props;
  const { accountData, savedQuestions, currentEventFormQuestions } =
    useSelector(getAccountState).account;
  // Snapshot of the form's questions at load, so option edits (which live in
  // currentEventFormQuestions, not eventSettings) register as a change on save.
  const [initialCurrentFormQuestions, setInitialCurrentFormQuestions] =
    useState<SavedFormQuestion[]>([]);
  const { theme } = useTheme();
  const dispatch = useDispatch();
  const [imageModal, setImageModal] = useState(false);
  const [sampleEventPhotos, setSampleEventPhotos] = useState<string[]>([]);
  const [redirectLink, setRedirectLink] = useState<{
    enabled: boolean;
    link: string;
    error: boolean;
  }>({ enabled: false, link: "", error: false });
  const [nameError, setNameError] = useState(false);
  const [paymentFailedVisible, setPaymentFailedVisible] = useState(false);
  const [backModal, setBackModal] = useState(false);
  const [alertText, setAlertText] = useState<string>("");
  const [collectInfoTourStep, setCollectInfoTourStep] = useState(1);

  const styles = {
    mediaFrame: {
      width: 200,
      borderRadius: 10,
      border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
      overflow: "hidden",
      position: "relative" as const,
      flexShrink: 0,
      alignSelf: "flex-start" as const,
    },
    mediaImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover" as const,
      objectPosition: "center",
      display: "block",
    },
    overlayButton: {
      backgroundColor: Colors.WHITE1,
      border: `0.5px solid ${Colors.GRAY2}`,
      borderRadius: 8,
      height: 30,
      width: 30,
      cursor: "pointer",
    },
  };

  const {
    updateEventSettings,
    postingEvent,
    isEditing,
    eventSettings,
    initialEventSettings,
    onPostEventPress,
    onSaveEventPress,
  } = useCreateEventManagement({
    event: existingForm,
    isEditing: existingForm !== undefined && !isCloning,
    type: eventType,
    setAlertText: setAlertText,
  });

  const { editor } = useRichTextEditor({
    body: eventSettings.body,
    styles: "height: 150px; overflow: scroll;",
    addCharacterCountExtension: {
      include: true,
      limit: 4000,
    },
  });

  const { charactersCount, contentJSON, isContentEmpty } = useEditorState({
    editor,
    selector: (context) => ({
      charactersCount: context.editor.storage.characterCount.characters(),
      contentJSON: context.editor.getJSON(),
      isContentEmpty: context.editor.isEmpty,
    }),
  });

  useAsyncOnMount(async () => {
    // Check if the subscription is Payment Failed{
    if (isSubscriptionPaymentFailed(accountData.customer.state)) {
      setPaymentFailedVisible(true);
    }

    if (existingForm) {
      setRedirectLink({
        enabled: existingForm.externalLink !== "",
        link: existingForm.externalLink,
        error: false,
      });
    } else if (defaultPublicLink) {
      updateEventSettings({ visibility: Visibility.Public });
    }

    if (eventType === EventType.GENERIC_LINK) {
      if (existingForm) {
        const formQuestions = await getEventFormQuestions(existingForm);
        dispatch(accountActions.updateCurrentEventFormQuestions(formQuestions));
        setInitialCurrentFormQuestions(formQuestions);
      }

      const photosRef = ref(firebaseStorage, "sampleEventPhotos/");
      await listAll(photosRef).then(async (listResult) => {
        const filteredImages = await filteredSampleImages(listResult);
        setSampleEventPhotos(filteredImages);
      });

      const essentialQuestion = savedQuestions.find(
        (question) => question.isEssential
      );
      if (!existingForm && essentialQuestion) {
        // add the essential question by default for new forms
        dispatch(
          accountActions.updateCurrentEventFormQuestions([essentialQuestion])
        );
      }
    }
  });

  const thumbnailImage = useMemo(
    () => getThumbnailImageUrl(eventSettings),
    [eventSettings]
  );

  const isTrackingLink = useMemo(
    () => eventType === EventType.GENERIC_LINK_NO_DATA,
    [eventType]
  );

  const madeChanges = useMemo(() => {
    if (isCloning) return true;
    const initialFormSettings = defaultPublicLink
      ? { ...initialEventSettings, visibility: Visibility.Public }
      : initialEventSettings;
    const initialForm = existingForm || initialFormSettings;
    if (initialForm) {
      // Fields to ignore when comparing
      const fieldsToIgnore: (keyof Event)[] = existingForm
        ? ["id", "createdAt"]
        : ["id", "createdAt", "customTickets"];

      const compareInitial = omit(initialForm, fieldsToIgnore);
      const compareCurrent = omit(eventSettings, fieldsToIgnore);

      if (isTrackingLink) {
        return !isEqual(compareInitial, compareCurrent);
      } else {
        // Editing a question's options changes currentEventFormQuestions but not
        // eventSettings, so it wouldn't otherwise register as a change on save.
        const questionsChanged =
          !!existingForm &&
          !isEqual(initialCurrentFormQuestions, currentEventFormQuestions);
        return (
          !isEqual(compareInitial, compareCurrent) ||
          questionsChanged ||
          (!existingForm
            ? !isContentEmpty
            : !isEqual(initialForm.body, contentJSON))
        );
      }
    }
    return false;
  }, [
    isCloning,
    defaultPublicLink,
    initialEventSettings,
    existingForm,
    eventSettings,
    isTrackingLink,
    isContentEmpty,
    contentJSON,
    currentEventFormQuestions,
    initialCurrentFormQuestions,
  ]);

  const confirmOnPress = useCallback(async () => {
    if (eventSettings.title === "") {
      setAlertText("Name is required.");
      setNameError(true);
      return;
    }
    // Check if link is valid
    if (isTrackingLink) {
      if (!eventSettings.externalLink) {
        setAlertText("There is no pasted link. You must add one to continue.");
        setRedirectLink({ ...redirectLink, error: true });
        return;
      }
      if (!checkValidUrl(eventSettings.externalLink)) {
        setAlertText(
          "The entered link URL is invalid. You must modify it to continue."
        );
        setRedirectLink({ ...redirectLink, error: true });
        return;
      }
    } else {
      if (redirectLink.link && !checkValidUrl(redirectLink.link)) {
        setAlertText(
          "The entered link URL is invalid. You must remove or modify it to continue."
        );
        setRedirectLink({ ...redirectLink, error: true });
        return;
      }
    }

    // Prevent any url link shortener from being used
    if (
      detectedLinkShortener(
        isTrackingLink ? eventSettings.externalLink : redirectLink.link
      )
    ) {
      setAlertText(
        "Your url includes a known url link shortener which is not allowed."
      );
      setRedirectLink({ ...redirectLink, error: true });
      return;
    }

    const editorData: JSONContent = editor.getJSON();
    if (!validateProductBody(editor)) {
      return;
    }

    // Modify the body field before handling save/post
    const finalEventSettings: Event = {
      ...eventSettings,
      body: editorData,
    };

    let success = true;
    if (isEditing) {
      success = await onSaveEventPress(finalEventSettings);
    } else {
      success = await onPostEventPress(finalEventSettings);
    }

    if (success) {
      if (onConfirmation) {
        onConfirmation(finalEventSettings.id);
      }

      setIsVisible(false);
    }
  }, [
    eventSettings,
    isTrackingLink,
    editor,
    isEditing,
    onConfirmation,
    setIsVisible,
    redirectLink,
    onSaveEventPress,
    onPostEventPress,
  ]);

  const confirmPasteLinkOnPress = useCallback(() => {
    if (detectedLinkShortener(redirectLink.link)) {
      setAlertText(
        "Your url includes a known url link shortener which is not allowed."
      );
      setRedirectLink({ ...redirectLink, error: true });
      return;
    }
    updateEventSettings({
      externalLink: eventSettings.externalLink ? "" : redirectLink.link,
    });
    if (eventSettings.externalLink) {
      setRedirectLink({
        enabled: redirectLink.enabled,
        link: "",
        error: false,
      });
    }
  }, [eventSettings.externalLink, redirectLink, updateEventSettings]);

  const handleBack = useCallback(() => {
    dispatch(accountActions.resetCurrentEventFormQuestions());
    setIsVisible(false);
  }, [dispatch, setIsVisible]);

  const backOnPress = useCallback(() => {
    if (madeChanges && !isCloning) {
      setBackModal(true);
    } else {
      handleBack();
    }
  }, [madeChanges, isCloning, handleBack]);

  const renderNameInput = useMemo(
    () => (
      <div className="ColumnNormal" style={{ gap: 7 }}>
        <span className="bodyMedium">Enter Name</span>
        <div className="ColumnNormal" style={{ gap: 4 }}>
          <CustomTextField
            value={eventSettings.title}
            placeholder={`Name your ${
              isTrackingLink ? "tracking link" : "form"
            }...`}
            inputMode="text"
            borderRadius={12}
            onChange={(name: any) => {
              updateEventSettings({ title: name.target.value });
              setNameError(false);
            }}
            maxLength={100}
            error={nameError}
            containerStyles={{
              width: undefined,
              alignSelf: undefined,
            }}
          />
          {nameError ? (
            <span className="smallBodySubtext" style={{ color: Colors.RED3 }}>
              Name is required
            </span>
          ) : null}
        </div>
      </div>
    ),
    [eventSettings.title, isTrackingLink, nameError, updateEventSettings]
  );

  const renderPasteLinkInput = useMemo(
    () => (
      <div className="ColumnNormal" style={{ gap: 7 }}>
        <span className="bodyMedium">
          {isTrackingLink ? "Paste Link" : "Paste Link for Redirect"}
        </span>
        {isTrackingLink ? (
          <span className="smallBodySubtext">
            We will automatically create a tracking link for the pasted url.
          </span>
        ) : null}
        <div className="AlignedRow" style={{ gap: 7 }}>
          <CustomTextField
            value={redirectLink.link}
            placeholder="https://..."
            inputMode="text"
            borderRadius={12}
            disabled={eventSettings.externalLink !== "" && !isTrackingLink}
            onChange={(name: any) => {
              setRedirectLink({
                enabled: redirectLink.enabled,
                link: name.target.value,
                error: false,
              });
              if (isTrackingLink) {
                updateEventSettings({ externalLink: name.target.value });
              }
            }}
            error={redirectLink.error}
          />
          {!isTrackingLink ? (
            <div>
              <RectangleButton
                buttonLabel={eventSettings.externalLink ? "Remove" : "Save"}
                onPress={confirmPasteLinkOnPress}
                altColor={
                  eventSettings.externalLink ? Colors.WHITE1 : Colors.BLACK
                }
                altTextColor={
                  eventSettings.externalLink ? Colors.RED1 : Colors.WHITE
                }
                disabled={!checkValidUrl(redirectLink.link)}
                containerStyles={{
                  paddingInline: 14,
                  paddingBlock: 11,
                  borderRadius: 12,
                  border: eventSettings.externalLink
                    ? `0.5px solid ${Colors.GRAY2}`
                    : undefined,
                }}
              />
            </div>
          ) : null}
        </div>
        {!redirectLink.error &&
        (checkValidUrl(eventSettings.externalLink) ||
          checkValidUrl(redirectLink.link)) ? (
          <div className="AlignedRow" style={{ gap: 7 }}>
            <Icon
              icon="ion:checkmark-circle"
              height={18}
              color={Colors.GREEN2}
            />
            <span className="bodyMedium" style={{ color: Colors.GREEN2 }}>
              Valid Url
            </span>
          </div>
        ) : redirectLink.link &&
          (!checkValidUrl(redirectLink.link) || redirectLink.error) ? (
          <div className="AlignedRow" style={{ gap: 7 }}>
            <Icon icon="ion:close-circle" height={18} color={Colors.RED3} />
            <span className="bodyMedium" style={{ color: Colors.RED3 }}>
              Invalid Url
            </span>
          </div>
        ) : (
          <span className="smallBodySubtext">
            Valid links include: websites, online files, web content, etc.
          </span>
        )}
      </div>
    ),
    [
      confirmPasteLinkOnPress,
      eventSettings.externalLink,
      isTrackingLink,
      redirectLink.enabled,
      redirectLink.error,
      redirectLink.link,
      updateEventSettings,
    ]
  );

  return (
    <LargePopupModalContainer
      showModal={isVisible}
      headerComp={
        <div className="AlignedRowSpaced">
          <div className="AlignedRow" style={{ gap: 20 }}>
            <BackButton iconName={"mdi:close"} onPress={backOnPress} />
            <span className="sectionTitle">
              {isTrackingLink
                ? isEditing
                  ? "Edit Tracking Link"
                  : "New Tracking Link"
                : isEditing
                  ? "Edit Form"
                  : "New Form"}
            </span>
          </div>
          <div className="onboarding-collect-info-create-target">
            <RectangleButton
              buttonLabel={isEditing ? "Save" : "Create"}
              onPress={confirmOnPress}
              loading={postingEvent}
              disabled={!madeChanges}
              containerStyles={{
                paddingInline: 14,
                paddingBlock: 10,
                borderRadius: 100,
              }}
              altColor={Colors.BLACK}
              altTextColor={Colors.WHITE}
            />
          </div>
        </div>
      }
      valueComp={
        <div
          className="HideScrollbar"
          style={{ height: "calc(100vh - 110px)", overflow: "auto" }}
        >
          {isTrackingLink ? (
            <div className="ColumnNormal" style={{ gap: 24 }}>
              {renderNameInput}
              <HorizontalDivider />
              {renderPasteLinkInput}
              <ThemeVisibilityOptions
                productSettings={eventSettings}
                updateProductSettings={updateEventSettings}
                displayOption="visibility"
                alignment="column"
              />
            </div>
          ) : (
            <>
              <div className="ColumnNormal" style={{ gap: 14 }}>
                <div
                  className="ColumnNormal onboarding-collect-info-name-target"
                  style={{ gap: 14 }}
                >
                  {renderNameInput}
                  <div className="ColumnNormal" style={{ gap: 7 }}>
                    <div className="AlignedRow" style={{ gap: 7 }}>
                      <span className="bodyMedium">Description</span>
                      <span
                        className="smallBodySubtext"
                        style={{ color: Colors.GRAY2 }}
                      >
                        Optional
                      </span>
                    </div>
                    <div
                      className="descriptionTextAreaContainer"
                      style={{
                        backgroundColor: Colors.TRANSPARENT,
                        borderWidth: 1,
                        borderColor: theme.EditableTextFieldBorder.borderColor,
                      }}
                    >
                      <CustomRichTextEditor
                        editor={editor}
                        headerComp={<></>}
                        isSimplified
                      />
                      <div
                        className="AlignedRowSpaced"
                        style={{ marginTop: 14 }}
                      >
                        <span className="bodySubtext">
                          {charactersCount}/{4000}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="ColumnNormal" style={{ gap: 7 }}>
                  <div className="AlignedRow" style={{ gap: 7 }}>
                    <span className="bodyMedium">Cover Photo</span>
                    <span
                      className="smallBodySubtext"
                      style={{ color: Colors.GRAY2 }}
                    >
                      Optional
                    </span>
                  </div>
                  {thumbnailImage ? (
                    <div
                      onClick={() => setImageModal(true)}
                      style={{
                        cursor: "pointer",
                        ...styles.mediaFrame,
                        aspectRatio:
                          eventSettings.mediaAspectRatio ||
                          MediaAspectRatio._1_1,
                      }}
                    >
                      <img
                        src={thumbnailImage}
                        alt="cover"
                        style={styles.mediaImage}
                      />
                      <div
                        className="ColumnNormal"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          gap: 4,
                          zIndex: 1,
                        }}
                      >
                        <div
                          className="Centering"
                          style={styles.overlayButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            updateEventSettings({
                              mediaURLs: [],
                              photoURL: "", // for backsupport
                            });
                          }}
                        >
                          <Icon
                            icon="ion:close"
                            height={16}
                            color={Colors.BLACK}
                          />
                        </div>
                        <MediaAspectRatioDropdown
                          productData={eventSettings}
                          setProductData={updateEventSettings}
                          containerStyle={styles.overlayButton}
                        />
                      </div>
                    </div>
                  ) : (
                    <div
                      className="Centering"
                      onClick={() => setImageModal(true)}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.opacity = "0.5")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.opacity = "1")
                      }
                      style={{
                        ...styles.mediaFrame,
                        aspectRatio: MediaAspectRatio._1_1,
                        borderStyle: "dashed",
                        borderColor: Colors.GRAY2,
                        cursor: "pointer",
                      }}
                    >
                      <Icon icon="ion:add" height={42} color={Colors.BLACK} />
                    </div>
                  )}
                </div>
              </div>
              <HorizontalDivider altMargin={24} />
              <div
                className="ColumnNormal onboarding-collect-info-formdata-target"
                style={{ gap: 14 }}
              >
                <div className="AlignedRow" style={{ gap: 7 }}>
                  <Icon
                    icon="ion:document-text"
                    height={20}
                    color={Colors.BLACK}
                  />
                  <span className="sectionTitle">Form Data</span>
                </div>
                <div className="ColumnNormal" style={{ gap: 14 }}>
                  <FormBuilderWrapper
                    isProfileQuestions={false}
                    hostId={eventSettings.createdBy}
                    event={eventSettings}
                    updateEventSettings={updateEventSettings}
                    altHeader={<></>}
                  />
                </div>
              </div>
              {!isEditing ? (
                collectInfoTourStep === 1 ? (
                  <OnboardingTourStep
                    key="collect-info-tour-step-1"
                    flowType={OnboardingFlowType.COLLECT_INFO}
                    target=".onboarding-collect-info-name-target"
                    content={
                      <>
                        Forms let you capture phone numbers and other info to
                        grow your audience and collect sign-ups.
                        <br />
                        <br />
                        Start with a title and description to give context on
                        what users are signing up for before they submit.
                      </>
                    }
                    stepNumber={1}
                    totalSteps={COLLECT_INFO_ONBOARDING_STEPS}
                    placement="left"
                    onStepComplete={() => setCollectInfoTourStep(2)}
                    mountDelayMs={300}
                    hideStepIndicator
                  />
                ) : collectInfoTourStep === 2 ? (
                  <OnboardingTourStep
                    key="collect-info-tour-step-2"
                    flowType={OnboardingFlowType.COLLECT_INFO}
                    target=".onboarding-collect-info-formdata-target"
                    content={
                      <>
                        Ask questions to gather information from subscribers.
                        <br />
                        <br />
                        Each answer is saved with that contact, so you can
                        segment and follow up based on what they submit.
                      </>
                    }
                    stepNumber={2}
                    totalSteps={COLLECT_INFO_ONBOARDING_STEPS}
                    placement="right"
                    onStepComplete={() => setCollectInfoTourStep(3)}
                    mountDelayMs={300}
                    hideStepIndicator
                  />
                ) : collectInfoTourStep === 3 ? (
                  <OnboardingTourStep
                    key="collect-info-tour-step-3"
                    flowType={OnboardingFlowType.COLLECT_INFO}
                    target=".onboarding-collect-info-redirect-target"
                    content={
                      <>
                        You can redirect people to an external link after they
                        submit. Useful for sending them to a website or landing
                        page.
                      </>
                    }
                    stepNumber={3}
                    totalSteps={COLLECT_INFO_ONBOARDING_STEPS}
                    placement="left"
                    onStepComplete={() => setCollectInfoTourStep(4)}
                    mountDelayMs={300}
                    hideStepIndicator
                  />
                ) : (
                  <OnboardingTourStep
                    key="collect-info-tour-step-4"
                    flowType={OnboardingFlowType.COLLECT_INFO}
                    target=".onboarding-collect-info-create-target"
                    content={
                      <>
                        A text automation will be created automatically upon
                        creation, which you can customize from the form&apos;s
                        dashboard.
                      </>
                    }
                    stepNumber={4}
                    totalSteps={COLLECT_INFO_ONBOARDING_STEPS}
                    placement="bottom"
                    mountDelayMs={300}
                    hideStepIndicator
                  />
                )
              ) : null}
              <HorizontalDivider altMargin={24} />
              <div
                className="ColumnNormal"
                style={{ gap: 24, paddingBottom: 100 }}
              >
                <div className="ColumnNormal" style={{ gap: 14 }}>
                  <div className="AlignedRow" style={{ gap: 7 }}>
                    <Icon icon="ion:cog" height={20} color={Colors.BLACK} />
                    <span className="sectionTitle">Settings</span>
                  </div>
                  <ThemeVisibilityOptions
                    productSettings={eventSettings}
                    updateProductSettings={updateEventSettings}
                    alignment="column"
                  />
                </div>
                <SwitchToggleItem
                  title="Accepting Responses"
                  toggleValue={!eventSettings.isManualSoldOut}
                  onChange={() =>
                    updateEventSettings({
                      isManualSoldOut: !eventSettings.isManualSoldOut,
                    })
                  }
                />
                <div
                  className="ColumnNormal onboarding-collect-info-redirect-target"
                  style={{ gap: 14 }}
                >
                  <SwitchToggleItem
                    title={isTrackingLink ? "Paste Link" : "Link Redirect"}
                    description={
                      isTrackingLink
                        ? "We will automatically create a tracking link for the pasted url"
                        : "Redirect people who submit form to an external link"
                    }
                    toggleValue={redirectLink.enabled}
                    onChange={() => {
                      setRedirectLink({
                        enabled: !redirectLink.enabled,
                        link: "",
                        error: false,
                      });
                      updateEventSettings({ externalLink: "" });
                    }}
                  />
                  {redirectLink.enabled ? renderPasteLinkInput : null}
                </div>
              </div>
            </>
          )}
          {imageModal ? (
            <CreateImageModal
              eventSettings={eventSettings}
              updateEventSettings={updateEventSettings}
              setIsImageModalOpen={setImageModal}
              sampleEventPhotos={sampleEventPhotos}
            />
          ) : null}
          <ConfirmDeleteModal
            heading="If you go back, your changes will be discarded."
            subtext="Continue?"
            deleteButtonText="Discard Changes"
            hideModal={!backModal}
            setIsVisible={setBackModal}
            deleteOnPress={handleBack}
          />
          {paymentFailedVisible ? (
            <PaymentFailedButtonModal
              location="Create Form"
              isVisible={paymentFailedVisible}
              setIsVisible={setPaymentFailedVisible}
              addOnClose={() => setIsVisible(false)}
            />
          ) : null}
          <AlertContainer
            headerComp={alertText}
            subHeaderComp={""}
            closeModal={() => {
              setAlertText("");
            }}
            hideModal={alertText === ""}
          />
        </div>
      }
    />
  );
};

export default CreateFormAndTrackingLinkModal;
